/**
 * Validation d'une réponse pour un marqueur (QCM + saisie complète).
 * Source unique pour relecture Firestore, agrégation live et saveAttempt.
 */
import { validateAnswerWithFunctions, DEGREE_TO_FUNCTIONS, normalizeCadence } from './riemannFunctions'
import { formatChordString as formatChordStringQcm } from './qcmOptions'
import { getChordFunction as getChordFunctionForLock } from './nodeCriteria'
import { parseChordDisplayString, getFigureValueFromParsedFigure, formatChordDetailed } from './chordFormatter'

/** Dernier segment « / I … VII » = pédale ; avant-dernier = emprunt (aligné sur formatChordString qcmOptions). */
const TRAILING_ROMAN_SLASH_RE = /^(.+)\s*\/\s*(I|II|III|IV|V|VI|VII)\s*$/i

/** Ex. « (ii V7) V » ou « (V7)IV » : après « ) », le degré romain = tonalité locale (comme slash explicite). */
const PAREN_GROUP_THEN_LOCAL_KEY_RE = /^\(([^)]*)\)\s*(VII|VI|IV|III|II|I|V)\s*$/i

/** Si le 2e jeton commence par un degré romain, c’est un nouvel accord (ex. « ii V7 »), pas le chiffrage du premier. */
const SECOND_TOKEN_IS_ROMAN_DEGREE_RE = /^(VII|VI|IV|III|II|I|V)(°|\+|\d|\/|$)/i

function peelTrailingRomanSuffixes(normalized) {
  let rest = normalized.trim()
  const suffixes = []
  for (let i = 0; i < 8; i++) {
    const m = rest.match(TRAILING_ROMAN_SLASH_RE)
    if (!m) break
    suffixes.push(m[2].toUpperCase())
    rest = m[1].trim()
  }
  return { core: rest, suffixes }
}

function suffixFieldBasesForQcm(suffixes, correctChord) {
  if (suffixes.length === 0) return [{}]
  const cPed = correctChord?.pedalDegree != null && String(correctChord.pedalDegree).trim() !== ''
    ? String(correctChord.pedalDegree).trim().toUpperCase()
    : ''
  const cBor = correctChord?.borrowedFromDegree != null && String(correctChord.borrowedFromDegree).trim() !== ''
    ? String(correctChord.borrowedFromDegree).trim().toUpperCase()
    : ''
  if (suffixes.length === 1) {
    const r = suffixes[0]
    if (cPed && !cBor && r === cPed) return [{ pedalDegree: r }]
    if (cBor && !cPed && r === cBor) return [{ borrowedFromDegree: r }]
    if (cPed && cBor) {
      if (r === cPed && r !== cBor) return [{ pedalDegree: r }]
      if (r === cBor && r !== cPed) return [{ borrowedFromDegree: r }]
    }
    if (r === cPed && r !== cBor) return [{ pedalDegree: r }]
    if (r === cBor && r !== cPed) return [{ borrowedFromDegree: r }]
    return [{ pedalDegree: r }, { borrowedFromDegree: r }]
  }
  if (suffixes.length >= 2) {
    const pedal = suffixes[0]
    const borrowed = suffixes[1]
    return [{ borrowedFromDegree: borrowed, pedalDegree: pedal }]
  }
  return [{}]
}

/**
 * Partie centrale (sans suffixes / I …) → objet accord minimal pour validateAnswerWithFunctions.
 * Aligné sur ChordSelectorModal.parseDisplayStringToChordData (sans selectedFunction).
 */
function parseQcmCoreToChordLike(core) {
  if (!core || typeof core !== 'string') return null
  const displayString = core.trim()
  if (!displayString) return null
  const { leading, figure } = parseChordDisplayString(displayString)
  const result = {}
  let figValue = null
  if (figure) {
    if (figure.stacked && figure.digits) {
      const joined = figure.digits.join('/')
      if (joined === '6/4') figValue = '64'
      else if (joined === '6/5') figValue = '65'
      else if (joined === '4/3') figValue = '43'
      else if (joined === '5/4') figValue = '54'
      else figValue = figure.digits[0]
    } else figValue = figure.value || null
  }
  result.figure = figValue || ''
  const leadingNorm = (leading && typeof leading === 'string') ? leading.trim() : ''
  if (leadingNorm === 'cad' || leadingNorm === 'Cad.') {
    result.degree = 'I'
    result.figure = '64'
    result.sixFourVariant = 'cadential'
    return result
  }
  if (leading === 'It+6' || leading === 'Fr+6' || leading === 'Gr+6') {
    result.specialRoot = leading === 'It+6' ? 'It' : leading === 'Fr+6' ? 'Fr' : 'Gr'
    result.figure = ''
    return result
  }
  let lead = leading
  if (lead && /^[♭#♯]/.test(lead)) {
    result.accidental = lead[0] === '♭' ? 'b' : '#'
    lead = lead.slice(1)
  }
  const romanMatch = lead && lead.match(/^(VII|VI|IV|III|II|I|V)(°?)$/i)
  if (romanMatch) {
    result.degree = romanMatch[1].toUpperCase()
    if (romanMatch[2] === '°') result.quality = '°'
    if (result.degree === 'II' && result.accidental === 'b') result.specialRoot = 'N'
    if (result.degree === 'II' && (figValue === '♭6' || figValue === 'b6')) {
      result.specialRoot = 'N'
      result.accidental = 'b'
      result.figure = ''
    }
    return result
  }
  if (leading && leading.includes('II') && (leading.includes('♭') || leading.includes('b'))) {
    result.degree = 'II'
    result.accidental = 'b'
    result.specialRoot = 'N'
    return result
  }
  return Object.keys(result).length > 0 ? result : null
}

/** Enlève une ou plusieurs couches de parenthèses englobantes si l’intérieur ne contient pas de « ( » / « ) » (ex. « (ii) », « (II/V) »). */
function unwrapLeadingTrailingParens(str) {
  let t = (str || '').trim()
  for (let i = 0; i < 8; i++) {
    if (!t.startsWith('(') || !t.endsWith(')')) break
    const inner = t.slice(1, -1).trim()
    if (!inner || /[()]/.test(inner)) break
    t = inner
  }
  return t
}

function isUsableQcmPartial(p) {
  return Boolean(p && (p.degree || p.specialRoot))
}

/**
 * Contenu entre parenthèses pouvant décrire plusieurs symboles (ex. « ii V7 ») : on ne valide que le premier accord,
 * avec emprunt / tonalité locale fournie par le degré après « ) ».
 */
function extractFirstChordStringFromPivotInner(inner) {
  const s = (inner || '').trim()
  if (!s) return ''
  const bare = unwrapLeadingTrailingParens(s)
  if (isUsableQcmPartial(parseQcmCoreToChordLike(bare))) return bare
  const parts = bare.split(/\s+/).filter(Boolean)
  if (parts.length >= 2 && !SECOND_TOKEN_IS_ROMAN_DEGREE_RE.test(parts[1])) {
    const merged = `${parts[0]}${parts[1]}`
    if (isUsableQcmPartial(parseQcmCoreToChordLike(merged))) return merged
  }
  if (parts.length >= 1 && isUsableQcmPartial(parseQcmCoreToChordLike(parts[0]))) return parts[0]
  return bare
}

function parseParenGroupWithTrailingLocalKey(normalized) {
  const m = normalized.match(PAREN_GROUP_THEN_LOCAL_KEY_RE)
  if (!m) return null
  return { inner: m[1].trim(), localKey: m[2].toUpperCase() }
}

/** Marqueurs découpés : « (ii » sans « ) », ou « V7) » sans « ( ». */
function stripSplitPivotParens(str) {
  let t = (str || '').trim()
  const o = t.indexOf('(')
  const c = t.indexOf(')')
  if (o >= 0 && c === -1) return t.slice(o + 1).trim()
  if (c >= 0 && o === -1) return t.slice(0, c).trim()
  return t
}

const ROMAN_LOCAL_KEYS_FOR_PIVOT = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII']

/** Degré romain (I–VII) issu d’une réponse élève stockée (saisie complète ou QCM). */
function degreeFromStoredUserAnswer(entry) {
  if (entry == null || typeof entry !== 'object') return null
  if (entry.degree != null && String(entry.degree).trim() !== '') {
    return String(entry.degree).toUpperCase()
  }
  if (entry.qcmAnswer != null && typeof entry.qcmAnswer === 'string') {
    const raw = entry.qcmAnswer.trim()
    if (!raw) return null
    const s = stripSplitPivotParens(raw)
    const firstTok = unwrapLeadingTrailingParens((s.split(/\s+/)[0] || '').trim())
    if (!firstTok) return null
    const partial = parseQcmCoreToChordLike(firstTok)
    if (partial?.degree) return String(partial.degree).toUpperCase()
  }
  return null
}

/** Degrés pour lesquels un marqueur *juste avant* un groupe emprunt peut partager la tonalité locale (parenthèse étendue). Exclut V / I pour éviter V6 → ii/V faux positifs. */
const PREFIX_BEFORE_BORROWED_ALLOWED_DEGREES = new Set(['II', 'III', 'IV', 'VI'])

/**
 * Groupe d’emprunts consécutifs d’après la **solution** (`chord.isBorrowed`).
 * La saisie élève (parenthèses) n’élargit pas encore cette frontière — uniquement l’auteur d’exercice / données.
 * @param {Array<{ chord?: object }|number>} markers
 * @returns {{ startIndex: number, endIndex: number }[]}
 */
export function computeSolutionBorrowedGroups(markers) {
  if (!Array.isArray(markers)) return []
  const out = []
  let start = null
  for (let i = 0; i < markers.length; i++) {
    const ch = typeof markers[i] === 'object' && markers[i]?.chord ? markers[i].chord : null
    const emprunt = ch && ch.isBorrowed === true
    if (emprunt) {
      if (start === null) start = i
    } else {
      if (start !== null) {
        out.push({ startIndex: start, endIndex: i - 1 })
        start = null
      }
    }
  }
  if (start !== null) out.push({ startIndex: start, endIndex: markers.length - 1 })
  return out
}

/**
 * Degré de la tonique locale **dans la tonique globale** à partir d’une réponse élève (objet accord ou QCM).
 * Pour un secondaire (ex. V7/V), `borrowedFromDegree` définit la tonalité locale (V), pas seulement `degree`.
 */
function localTonicDegreeInGlobalKeyFromStudentEntry(entry) {
  if (entry == null || typeof entry !== 'object') return null
  const bor =
    entry.borrowedFromDegree != null && String(entry.borrowedFromDegree).trim() !== ''
      ? String(entry.borrowedFromDegree).toUpperCase()
      : ''
  if (bor && ROMAN_LOCAL_KEYS_FOR_PIVOT.includes(bor)) return bor
  if (entry.degree != null && String(entry.degree).trim() !== '') {
    const d = String(entry.degree).toUpperCase()
    if (ROMAN_LOCAL_KEYS_FOR_PIVOT.includes(d)) return d
  }
  return degreeFromStoredUserAnswer(entry)
}

/**
 * Tonalité locale implicite lue sur le marqueur d’indice `anchorIndex` (réponse élève uniquement ; politique B).
 */
function implicitLocalKeyFromStudentAtGlobalIndex(markers, anchorIndex, userAnswersByIndex) {
  if (anchorIndex < 0 || anchorIndex >= markers.length) return null
  const key = localTonicDegreeInGlobalKeyFromStudentEntry(userAnswersByIndex?.[anchorIndex])
  return key && ROMAN_LOCAL_KEYS_FOR_PIVOT.includes(key) ? key : null
}

/**
 * Tonalité locale (degré romain I–VII dans la tonique globale) pour tous les marqueurs d’un groupe emprunt :
 * = degré romain **saisi par l’élève** sur le premier marqueur après la fin du groupe (pas d’inférence depuis la solution).
 *
 * **Préfixe parenthèse étendue :** si l’accord courant n’a pas `isBorrowed` dans la solution mais est
 * immédiatement **avant** le premier marqueur d’un groupe emprunt (ex. `vi` puis `(V7/V)`), on applique
 * la même tonalité locale que pour ce groupe — aligné sur une parenthèse géante côté élève.
 *
 * @param {Array} markers - exercise.markers
 * @param {number} markerIndex
 * @param {Array<object|null>|null} pedalAwareChords - optionnel, longueur alignée sur markers (ex. pedalAwareCorrectAnswers)
 * @param {Record<number, object>|object[]|null} userAnswersByIndex - réponses élève : ancre = marqueur **juste après** la chaîne d’emprunt, ou pour un **préfixe** avant le groupe, marqueur **premier du groupe** (accord suivant la case courante).
 * @returns {string|null} ex. 'V'
 */
export function getImplicitBorrowedLocalKeyForMarker(markers, markerIndex, pedalAwareChords = null, userAnswersByIndex = null) {
  if (!Array.isArray(markers) || markerIndex < 0) return null
  const groups = computeSolutionBorrowedGroups(markers)
  const curCh = typeof markers[markerIndex] === 'object' && markers[markerIndex]?.chord ? markers[markerIndex].chord : null
  const curDeg = curCh?.degree ? String(curCh.degree).toUpperCase() : ''
  const gIn = groups.find((gr) => markerIndex >= gr.startIndex && markerIndex <= gr.endIndex)
  let result = null
  if (gIn) {
    const anchorAfterChain = gIn.endIndex + 1
    result = implicitLocalKeyFromStudentAtGlobalIndex(markers, anchorAfterChain, userAnswersByIndex)
  } else {
    const nextCh = typeof markers[markerIndex + 1] === 'object' && markers[markerIndex + 1]?.chord ? markers[markerIndex + 1].chord : null
    if (
      PREFIX_BEFORE_BORROWED_ALLOWED_DEGREES.has(curDeg) &&
      nextCh &&
      nextCh.isBorrowed === true
    ) {
      const prefixGroup = groups.find((gr) => gr.startIndex === markerIndex + 1)
      if (prefixGroup) {
        // Ancre = premier accord **emprunté** (case juste après le préfixe), ex. V7/V après (ii)/vi — pas le marqueur après tout le groupe.
        const anchorImmediateNext = prefixGroup.startIndex
        result = implicitLocalKeyFromStudentAtGlobalIndex(markers, anchorImmediateNext, userAnswersByIndex)
      }
    }
  }
  return result
}

/**
 * Parse une saisie QCM en accord romain + bases slash (pédale / emprunt explicite).
 * @returns {{ partial: object, baseList: object[] }|null}
 */
function parseQcmPivotPartialBases(qcmStr, correctChord) {
  if (!qcmStr || typeof qcmStr !== 'string') return null
  const trimmedIn = qcmStr
    .normalize('NFKC')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!trimmedIn) return null
  let s = stripSplitPivotParens(trimmedIn)
  s = unwrapLeadingTrailingParens(s)
  const { core, suffixes } = peelTrailingRomanSuffixes(s)
  const coreBare = unwrapLeadingTrailingParens(core)
  const partial = parseQcmCoreToChordLike(coreBare)
  if (!isUsableQcmPartial(partial) || partial.specialRoot) return null
  if (partial.accidental) return null
  const bases = suffixFieldBasesForQcm(suffixes, correctChord)
  const baseList = bases.length > 0 ? bases : [{}]
  return { partial, baseList }
}

function tryQcmPivotWithImplicitLocalKey(qcmStr, correctChord, tonality, dominantMode, implicitKey) {
  if (!implicitKey || !qcmStr || !correctChord) return null
  const parsed = parseQcmPivotPartialBases(qcmStr, correctChord)
  if (!parsed) return null
  const opts = {
    functionOnlyAvailable: false,
    tonality: tonality || 'C',
    mode: dominantMode || 'major'
  }
  for (const base of parsed.baseList) {
    const cand = { ...parsed.partial, ...base, borrowedFromDegree: String(implicitKey).toUpperCase() }
    const v = validateAnswerWithFunctions(cand, correctChord, null, opts)
    if (v.level === 1) return { ok: true, validation: v }
  }
  return null
}

function buildQcmChordCandidatesFromString(qcmStr, correctChord, dominantMode = 'major') {
  if (!qcmStr || typeof qcmStr !== 'string') return []
  const trimmedIn = qcmStr
    .normalize('NFKC')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  if (!trimmedIn) return []
  let normalized = trimmedIn
  normalized = unwrapLeadingTrailingParens(normalized)
  const candidates = []

  const parenPivot = parseParenGroupWithTrailingLocalKey(normalized)
  if (parenPivot) {
    const firstChordStr = extractFirstChordStringFromPivotInner(parenPivot.inner)
    const coreBare = unwrapLeadingTrailingParens(firstChordStr)
    const partial = parseQcmCoreToChordLike(coreBare)
    if (isUsableQcmPartial(partial)) {
      candidates.push({ ...partial, borrowedFromDegree: parenPivot.localKey })
    }
  }

  const { core, suffixes } = peelTrailingRomanSuffixes(normalized)
  const coreBare = unwrapLeadingTrailingParens(core)
  const bases = suffixFieldBasesForQcm(suffixes, correctChord)
  for (const base of bases) {
    const partial = parseQcmCoreToChordLike(coreBare)
    if (!partial) continue
    const merged = { ...partial, ...base }
    if (merged.degree || merged.specialRoot) candidates.push(merged)
  }

  return candidates
}

/**
 * @param {object} [structuralOptions]
 * @param {string|null} [structuralOptions.implicitBorrowedFromDegree] - tonalité locale imposée (groupe emprunt : degré du marqueur suivant la parenthèse)
 */
export function qcmAnswerMatchesChordStructurally(qcmStr, correctChord, tonality, dominantMode, structuralOptions = {}) {
  const implicitKey = structuralOptions.implicitBorrowedFromDegree ?? null
  const opts = {
    functionOnlyAvailable: false,
    tonality: tonality || 'C',
    mode: dominantMode || 'major'
  }

  const candidates = buildQcmChordCandidatesFromString(qcmStr, correctChord, dominantMode)
  for (const cand of candidates) {
    const val = validateAnswerWithFunctions(cand, correctChord, null, opts)
    if (val.level === 1) return { ok: true, validation: val }
  }

  if (implicitKey) {
    const implicitMatch = tryQcmPivotWithImplicitLocalKey(qcmStr, correctChord, opts.tonality, opts.mode, implicitKey)
    if (implicitMatch) return implicitMatch
    return null
  }

  return null
}

/**
 * Mode saisie complète (`full`) : après échec de validateAnswerWithFunctions sur l’objet accord,
 * réessaie avec tonalité locale implicite (groupe parenthèse) puis compare via la même chaîne que le QCM.
 */
export function validateFullChordObjectWithPivotFallback(
  userAnswer,
  correctAnswer,
  progressionMode,
  tonality,
  dominantMode,
  implicitBorrowedFromDegree = null
) {
  const opts = {
    functionOnlyAvailable: progressionMode === 'functions',
    tonality: tonality || 'C',
    mode: dominantMode || 'major'
  }
  const primary = validateAnswerWithFunctions(
    userAnswer,
    correctAnswer,
    userAnswer.selectedFunction || userAnswer.function || null,
    opts
  )
  if (primary.level === 1 || progressionMode !== 'full') return primary

  const implicit =
    implicitBorrowedFromDegree != null && String(implicitBorrowedFromDegree).trim() !== ''
      ? String(implicitBorrowedFromDegree).toUpperCase()
      : null
  const hasExplicitBor =
    userAnswer.borrowedFromDegree != null && String(userAnswer.borrowedFromDegree).trim() !== ''

  if (implicit && !hasExplicitBor) {
    const merged = { ...userAnswer, borrowedFromDegree: implicit }
    const retry = validateAnswerWithFunctions(
      merged,
      correctAnswer,
      merged.selectedFunction || merged.function || null,
      opts
    )
    if (retry.level === 1) return retry
  }

  const label = formatChordDetailed(userAnswer)
  if (label && label !== 'Non répondu') {
    const structural = qcmAnswerMatchesChordStructurally(
      label,
      correctAnswer,
      opts.tonality,
      opts.mode,
      implicit ? { implicitBorrowedFromDegree: implicit } : {}
    )
    if (structural?.ok) return structural.validation
  }

  return primary
}

export function canonicalizeQcmAnswerString(value) {
  if (typeof value !== 'string') return ''
  const normalized = value
    .normalize('NFKC')
    .replace(/\u00A0/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\s*\/\s*/g, '/')
    .trim()
  if (!normalized) return ''
  if (/^cad\.?/i.test(normalized)) {
    return normalized.replace(/^cad\.?/i, 'Cad.').toUpperCase()
  }
  const romanPrefix = normalized.match(/^([#♯b♭]?)(vii|vi|iv|iii|ii|i|v)(.*)$/i)
  if (!romanPrefix) return normalized
  const accidental = romanPrefix[1] || ''
  const degree = (romanPrefix[2] || '').toUpperCase()
  const rest = romanPrefix[3] || ''
  return `${accidental}${degree}${rest}`
}

const SPECIAL_ROOT_TO_FUNCTION = {
  N: 'SD',
  It: 'D',
  Fr: 'D',
  Gr: 'D'
}

function chordToParcoursKeyStatic(chord) {
  if (!chord) return null
  if (chord.specialRoot === 'N') return 'N6'
  if (chord.specialRoot === 'It' || chord.specialRoot === 'Fr' || chord.specialRoot === 'Gr') return chord.specialRoot
  const degree = (chord.degree || '').toUpperCase()
  if (!degree) return null
  const fig = chord.figure && chord.figure !== '5' ? chord.figure : ''
  if (degree === 'I' && fig === '64') {
    if (chord.sixFourVariant === 'passing') return 'V64'
    if (chord.sixFourVariant === 'cadential') return 'cad64'
    return 'I64'
  }
  return degree + fig
}

function getFunctionFromQcmAnswerStatic(qcmAnswer) {
  if (!qcmAnswer || typeof qcmAnswer !== 'string') return null
  const s = qcmAnswer.trim()
  if (s.startsWith('It+6') || s.startsWith('Fr+6') || s.startsWith('Gr+6')) return 'D'
  if (s.startsWith('II♭') || s.startsWith('IIb')) return 'SD'
  if (s.startsWith('cad') || s.startsWith('Cad.')) return 'D'
  const degreeMatch = s.match(/^(III|VII|II|IV|VI|I|V)(♭|♯|b|#)?/)
  const degree = degreeMatch ? degreeMatch[1] : null
  if (degree) {
    const functions = DEGREE_TO_FUNCTIONS[degree] || []
    return functions.length > 0 ? functions[0] : null
  }
  return null
}

function getChordFunctionForStoredAnswer(chord) {
  if (!chord) return null
  const { degree, specialRoot, selectedFunction, function: qcmFunction, qcmAnswer } = chord
  if (qcmFunction && ['T', 'SD', 'D'].includes(qcmFunction)) {
    return qcmFunction
  }
  if (selectedFunction) {
    return selectedFunction
  }
  if (specialRoot) {
    return SPECIAL_ROOT_TO_FUNCTION[specialRoot] || null
  }
  if (degree === 'I' && chord.figure === '64' && (chord.sixFourVariant === 'passing' || chord.sixFourVariant === 'cadential')) {
    return 'D'
  }
  if (degree) {
    const functions = DEGREE_TO_FUNCTIONS[degree] || []
    return functions.length > 0 ? functions[0] : null
  }
  if (qcmAnswer) {
    return getFunctionFromQcmAnswerStatic(qcmAnswer)
  }
  return null
}

/**
 * @param {object} params
 * @param {object|null} params.userAnswer
 * @param {object|null} params.correctAnswer
 * @param {number} params.index
 * @param {string} params.progressionMode - 'qcm' | 'functions' | 'full'
 * @param {string} params.exerciseMode - 'parcours' | 'libre'
 * @param {object|null} params.parcoursContext
 * @param {object|null} params.cachedValidation - si fourni, renvoyé tel quel
 * @param {boolean} params.isLocked - case parcours non remplissable
 * @param {string} params.tonality
 * @param {string} params.dominantMode
 * @param {string|null} [params.implicitBorrowedFromDegree] - I–VII : tonalité locale pour tout un groupe emprunt (marqueur suivant la parenthèse)
 * @returns {object|null} validation ou null si rien à enregistrer
 */
export function computeSingleMarkerValidation({
  userAnswer,
  correctAnswer,
  index: _index,
  progressionMode,
  exerciseMode,
  parcoursContext,
  cachedValidation,
  isLocked,
  tonality,
  dominantMode,
  implicitBorrowedFromDegree = null
}) {
  if (cachedValidation) return cachedValidation

  if (userAnswer && correctAnswer) {
    const qcmStr = userAnswer && typeof userAnswer === 'object' && userAnswer.qcmAnswer != null
      ? String(userAnswer.qcmAnswer).trim()
      : ''
    const hasQcmPayload = progressionMode === 'qcm' && qcmStr !== ''

    if (hasQcmPayload) {
      const key = chordToParcoursKeyStatic(correctAnswer)
      const unlocked = parcoursContext?.unlockedChordKeys
      const expectFunctionOnly = exerciseMode === 'parcours'
        && Array.isArray(unlocked)
        && unlocked.length > 0
        && key != null
        && !unlocked.includes(key)
      const correctStr = expectFunctionOnly ? (getChordFunctionForLock(correctAnswer) || '') : formatChordStringQcm(correctAnswer)
      const canonicalUser = canonicalizeQcmAnswerString(qcmStr)
      const canonicalCorrect = canonicalizeQcmAnswerString(correctStr)
      let isCorrectChord = userAnswer.qcmAnswer === correctStr || canonicalUser === canonicalCorrect
        || (expectFunctionOnly && (userAnswer.function === correctStr))
      let qcmEquivNotation = false
      if (!isCorrectChord && !expectFunctionOnly) {
        const structural = qcmAnswerMatchesChordStructurally(qcmStr, correctAnswer, tonality, dominantMode, {
          ...(implicitBorrowedFromDegree
            ? { implicitBorrowedFromDegree: String(implicitBorrowedFromDegree).toUpperCase() }
            : {})
        })
        if (structural?.ok) {
          isCorrectChord = true
          // Même logique que le mode complet : pédale, pivot (hauteurs), etc. — la chaîne QCM ne coïncide pas avec la référence.
          qcmEquivNotation = true
        }
      }
      const correctFunction = getChordFunctionForStoredAnswer(correctAnswer)
      const userFunction = userAnswer.function ?? null
      const isCorrectFunction = Boolean(userFunction && correctFunction && userFunction === correctFunction)
      const userFunctionFromChord = qcmStr ? getFunctionFromQcmAnswerStatic(qcmStr) : null
      const isCorrectFunctionFromChord = Boolean(userFunctionFromChord && correctFunction && userFunctionFromChord === correctFunction)
      let cadenceBonus = 0
      const correctCadence = correctAnswer.cadence ? normalizeCadence(correctAnswer.cadence) : null
      const unlockedBatch = parcoursContext?.unlockedCadenceValues
      const effectiveCorrectCadenceBatch = (exerciseMode === 'parcours' && Array.isArray(unlockedBatch) && unlockedBatch.length > 0)
        ? (correctCadence && unlockedBatch.includes(correctCadence) ? correctCadence : null)
        : correctCadence
      const userCadence = userAnswer.cadence ? normalizeCadence(userAnswer.cadence) : null
      if (effectiveCorrectCadenceBatch && userCadence && userCadence === effectiveCorrectCadenceBatch) cadenceBonus = 10
      const score = isCorrectChord ? 100 + cadenceBonus : (isCorrectFunctionFromChord || isCorrectFunction ? 50 : 0)
      const cadenceTail = effectiveCorrectCadenceBatch ? (cadenceBonus === 10 ? '' : userCadence ? ' Cadence incorrecte.' : ' Cadence manquante.') : ''
      const feedback = (isCorrectChord
        ? (qcmEquivNotation
          ? (cadenceBonus > 0 ? 'Notation équivalente acceptée. + Bonus cadence' : 'Notation équivalente acceptée.')
          : (cadenceBonus > 0 ? 'Parfait ! + Bonus cadence' : 'Correct !'))
        : (isCorrectFunctionFromChord || isCorrectFunction)
          ? 'Partiel : bonne fonction.'
          : 'Incorrect') + cadenceTail
      const level = isCorrectChord ? 1 : (isCorrectFunctionFromChord || isCorrectFunction ? 0.5 : 0)
      return { level, score, cadenceBonus, feedback }
    }

    return validateFullChordObjectWithPivotFallback(
      userAnswer,
      correctAnswer,
      progressionMode,
      tonality,
      dominantMode,
      implicitBorrowedFromDegree
    )
  }

  if (correctAnswer && !isLocked) {
    return {
      level: 0,
      score: 0,
      cadenceBonus: 0,
      feedback: 'Réponse manquante'
    }
  }

  return null
}

/**
 * Infère le mode de saisie à partir d’une réponse stockée (tentatives historiques
 * n’ont souvent pas de progressionMode persisté).
 */
export function inferProgressionModeFromUserAnswer(userAnswer, hint = null) {
  if (hint === 'qcm' || hint === 'functions' || hint === 'full') return hint
  if (userAnswer && typeof userAnswer === 'object') {
    const qcm = userAnswer.qcmAnswer
    if (qcm != null && String(qcm).trim() !== '') return 'qcm'
    const hasDegree = !!(userAnswer.degree || userAnswer.root)
    if (!hasDegree && (userAnswer.selectedFunction || userAnswer.function)) return 'functions'
  }
  return 'full'
}

/** Niveau partiel (QCM fonction seule = 0.5, ou niveaux 2/3 du mode complet). */
export function isPartialValidationLevel(level) {
  return level === 0.5 || level === 2 || level === 3
}

/**
 * Revalide une réponse d’une tentative déjà sauvegardée (Suivi, stats, modale).
 * Aligne le chemin sur saveAttempt / computeSingleMarkerValidation (y compris QCM).
 */
export function revalidateStoredMarkerAnswer(userAnswer, correctAnswer, options = {}) {
  if (!correctAnswer) {
    return { level: 0, score: 0, cadenceBonus: 0, feedback: 'Réponse manquante' }
  }
  if (!userAnswer) {
    return { level: 0, score: 0, cadenceBonus: 0, feedback: 'Réponse manquante' }
  }
  const progressionMode = inferProgressionModeFromUserAnswer(
    userAnswer,
    options.progressionMode ?? null
  )
  const validation = computeSingleMarkerValidation({
    userAnswer,
    correctAnswer,
    index: options.index ?? 0,
    progressionMode,
    exerciseMode: options.exerciseMode || 'libre',
    parcoursContext: options.parcoursContext ?? null,
    cachedValidation: null,
    isLocked: false,
    tonality: options.tonality || 'C',
    dominantMode: options.mode || options.dominantMode || 'major',
    implicitBorrowedFromDegree: options.implicitBorrowedFromDegree ?? null
  })
  return (
    validation || {
      level: 0,
      score: 0,
      cadenceBonus: 0,
      feedback: 'Réponse manquante'
    }
  )
}
